/**
 * Attribution context for contact/lead events (Milestone 15).
 *
 * Intentionally avoids importing vehicleSearch parsers — that path forms a
 * circular graph (attribution → seo → vehicleSearch → buyerAnalytics → attribution)
 * that can leave named exports undefined during module init / HMR.
 */
import { getStoredAttributionContext } from "../utils/campaignAttribution";
import { sanitizeAnalyticsPayload } from "./sanitize";

const STRIP_QUERY_KEYS = new Set(["token", "previewToken", "email", "phone", "name", "password"]);

function readUtmParams(search = "") {
  const sp = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return sanitizeAnalyticsPayload({
    utmSource: sp.get("utm_source") || null,
    utmMedium: sp.get("utm_medium") || null,
    utmCampaign: sp.get("utm_campaign") || null,
    utmContent: sp.get("utm_content") || null,
    utmTerm: sp.get("utm_term") || null,
  });
}

function pickParam(sp, ...keys) {
  for (const key of keys) {
    const value = sp.get(key);
    if (value != null && value !== "") return value;
  }
  return null;
}

function pickNumberParam(sp, ...keys) {
  const raw = pickParam(sp, ...keys);
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Lightweight search-query snapshot for analytics — no vehicleSearch imports. */
function safeSearchParamsSnapshot(search = "") {
  const sp = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  for (const key of [...sp.keys()]) {
    if (STRIP_QUERY_KEYS.has(key.toLowerCase())) sp.delete(key);
  }
  return sanitizeAnalyticsPayload({
    city: pickParam(sp, "city"),
    make: pickParam(sp, "make"),
    model: pickParam(sp, "model"),
    bodyType: pickParam(sp, "bodyType", "body_type"),
    transmission: pickParam(sp, "transmission"),
    sort: pickParam(sp, "sort"),
    page: pickNumberParam(sp, "page"),
    pageSize: pickNumberParam(sp, "pageSize", "page_size"),
    priceMinCop: pickNumberParam(sp, "priceMinCop", "price_min", "minPrice"),
    priceMaxCop: pickNumberParam(sp, "priceMaxCop", "price_max", "maxPrice"),
    keyword: pickParam(sp, "keyword", "q", "search"),
  });
}

export function buildAttributionContext(extra = {}) {
  if (typeof window === "undefined") {
    return sanitizeAnalyticsPayload(extra);
  }

  let stored = {};
  try {
    const ctx = getStoredAttributionContext();
    const first = ctx.firstTouch || {};
    const last = ctx.lastTouch || first;
    stored = {
      firstTouchSource: first.source,
      firstTouchMedium: first.medium,
      lastTouchSource: last.source,
      lastTouchMedium: last.medium,
      utmCampaign: last.utmCampaign || first.utmCampaign,
      landingPage: ctx.landingPage,
      landingPageType: ctx.landingPageType,
      seoPageType: first.seoPageType || last.seoPageType,
      seoPageSlug: first.seoPageSlug || last.seoPageSlug,
      attributionSessionId: ctx.attributionSessionId,
      attributionVisitorId: ctx.attributionVisitorId,
    };
  } catch {
    stored = {};
  }

  const utm = readUtmParams(window.location.search);

  return sanitizeAnalyticsPayload({
    currentUrl: window.location.href.split("#")[0],
    path: window.location.pathname,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    searchParamsSnapshot: safeSearchParamsSnapshot(window.location.search),
    ...stored,
    ...utm,
    ...extra,
  });
}
